import React, { useCallback, useMemo } from "react";

import {
    AppBar,
    Authenticator,
    CircularProgressCenter,
    Drawer,
    FireCMS,
    FireCMSi18nProvider,
    ModeControllerProvider,
    NavigationRoutes,
    Scaffold,
    SideDialogs,
    SnackbarProvider,
    useBuildLocalConfigurationPersistence,
    useBuildModeController,
    useBuildNavigationController,
    useValidateAuthenticator
} from "@firecms/core";
import {
    FirebaseAuthController,
    FirebaseLoginView,
    FirebaseSignInProvider,
    FirebaseUserWrapper,
    useFirebaseAuthController,
    useFirebaseStorageSource,
    useFirestoreDelegate,
    useInitialiseFirebase,
} from "@firecms/firebase";
import { CenteredView } from "@firecms/ui";

import { firebaseConfig } from "./config/firebase_config";
import {
    devicesCollection,
    feedbacksCollection,
    settingsCollection,
    usersCollection,
} from "./collections";
import { confirmClosePlugin } from "./plugins/confirm_close_plugin";
import { deleteConfirmPlugin } from "./plugins/delete_confirm_plugin";
import { HomePageWithStats } from "./home/HomePageWithStats";
import { firecmsTr } from "./i18n/tr";
import { LocaleToggle } from "./i18n/LocaleToggle";

/**
 * Octo AI — FireCMS admin paneli.
 *
 * Firestore / Storage güvenlik kuralları Firebase Console'dan yönetilir
 * (isAdmin e-posta listesi buradaki allowedAdmins ile aynı tutulmalıdır).
 */
function App() {

    /**
     * FireCMS giriş kapısı: Firebase Auth başarılı olsa bile
     * yalnızca allowlist'teki e-postalar panele girebilir.
     */
    const myAuthenticator: Authenticator<FirebaseUserWrapper> = useCallback(async ({
        user,
    }) => {

        const allowedAdmins = [
            "codeblocstudio@gmail.com",
        ];

        const userEmail = user?.email?.toLowerCase();
        const isAuthorized = userEmail ? allowedAdmins.includes(userEmail) : false;

        if (isAuthorized) {
            console.log(`✅ Yönetici girişi başarılı: ${userEmail}`);
            return true;
        } else {
            console.warn(`🚨 Yetkisiz giriş denemesi: ${userEmail}`);
            throw Error("Bu panele erişim yetkiniz bulunmamaktadır!");
        }
    }, []);

    const collections = useMemo(() => [
        usersCollection,
        devicesCollection,
        feedbacksCollection,
        settingsCollection,
    ], []);

    const {
        firebaseApp,
        firebaseConfigLoading,
        configError
    } = useInitialiseFirebase({
        firebaseConfig
    });

    const modeController = useBuildModeController();

    const signInOptions: FirebaseSignInProvider[] = ["google.com"];

    const authController: FirebaseAuthController = useFirebaseAuthController({
        firebaseApp,
        signInOptions
    });

    const userConfigPersistence = useBuildLocalConfigurationPersistence();

    const firestoreDelegate = useFirestoreDelegate({
        firebaseApp
    });

    const storageSource = useFirebaseStorageSource({
        firebaseApp
    });

    const {
        authLoading,
        canAccessMainView,
        notAllowedError
    } = useValidateAuthenticator({
        authController,
        authenticator: myAuthenticator,
        dataSourceDelegate: firestoreDelegate,
        storageSource
    });

    const navigationController = useBuildNavigationController({
        disabled: authLoading,
        collections,
        authController,
        dataSourceDelegate: firestoreDelegate,
        plugins: [confirmClosePlugin, deleteConfirmPlugin],
    });

    if (firebaseConfigLoading || !firebaseApp) {
        return <>
            <CircularProgressCenter />
        </>;
    }

    if (configError) {
        return <CenteredView>{configError}</CenteredView>;
    }

    return (
        <FireCMSi18nProvider
            locale="tr"
            translations={{
                tr: firecmsTr,
            }}
        >
            <SnackbarProvider>
                <ModeControllerProvider value={modeController}>
                    <FireCMS
                        locale="tr"
                        dateTimeFormat="dd MMMM yyyy, HH:mm"
                        navigationController={navigationController}
                        authController={authController}
                        userConfigPersistence={userConfigPersistence}
                        dataSourceDelegate={firestoreDelegate}
                        storageSource={storageSource}
                    >
                        {({
                            loading
                        }) => {

                            if (loading || authLoading) {
                                return <CircularProgressCenter size={"large"} />;
                            }

                            if (!canAccessMainView) {
                                return <FirebaseLoginView authController={authController}
                                    firebaseApp={firebaseApp}
                                    signInOptions={signInOptions}
                                    notAllowedError={notAllowedError} />;
                            }

                            return <Scaffold
                                autoOpenDrawer={false}>
                                <AppBar
                                    title={"Octo AI Admin Panel"}
                                    endAdornment={<LocaleToggle />}
                                />
                                <Drawer />
                                <NavigationRoutes homePage={<HomePageWithStats />} />
                                <SideDialogs />
                            </Scaffold>;
                        }}
                    </FireCMS>
                </ModeControllerProvider>
            </SnackbarProvider>
        </FireCMSi18nProvider>
    );

}

export default App;
